import React from 'react';
import { Link } from 'react-router-dom';

import { SceneAgents, SceneEditor, SceneK8s } from './FeatureScenes';
import { HandoffFigure } from './HumanAiSection';
import { DesignPlate, FlowPlate, GitPlate, PlanPlate, PromptPlate } from './ProcessFigures';

import './PersonaSection.css';

/*
 * Persona router - "pick who you are" tabs, each opening an Apple-style
 * bento grid. Visual tiles embed the SAME figures used at the place the
 * tile points to (process plates, the kanban handoff board, the feature
 * scenes) - no bespoke tile art. Every tile is a link: hash tiles
 * smooth-scroll to the matching section/feature/step anchor, route
 * tiles go to /signup, /pricing, /explore or the VS Code extension.
 *
 * CSS-only radio tabs: works with JavaScript disabled, all panels stay
 * in the DOM for crawlers, default tab checked in prerendered HTML.
 */

const PEEKS: Record<string, React.ReactNode> = {
  prompt: <PromptPlate />,
  design: <DesignPlate />,
  flow: <FlowPlate />,
  plan: <PlanPlate />,
  git: <GitPlate />,
  board: <HandoffFigure />,
  editor: <SceneEditor active={false} reduced />,
  agents: <SceneAgents active={false} reduced />,
  k8s: <SceneK8s active={false} reduced />,
};

interface Tile {
  size: 'lg' | 'mid' | 'wide' | 'sm';
  accent?: boolean;
  peek?: string;
  title: string;
  blurb?: string;
  href: string;
}

interface Persona {
  key: string;
  tab: string;
  icon: string;
  tiles: Tile[];
}

const PERSONAS: Persona[] = [
  {
    key: 'nontech',
    tab: 'Non-technical',
    icon: 'fa-solid fa-comment-dots',
    tiles: [
      {
        size: 'lg', peek: 'prompt', href: '#process',
        title: 'Describe it. Ship it.',
        blurb: 'One sentence in, a real product out - a clickable design in as little as two minutes, then nine gated steps to a live app.',
      },
      {
        size: 'mid', peek: 'design', href: '#step-05',
        title: 'Designs you can click and edit',
      },
      { size: 'sm', href: '#step-09', title: 'Live on a URL' },
      { size: 'sm', href: '#step-02', title: 'Approve every step' },
      { size: 'wide', accent: true, href: '/signup', title: 'Start free - with one sentence' },
      { size: 'sm', href: '#collaboration', title: 'A board you can read' },
      { size: 'sm', href: '#trust', title: 'Who’s behind it' },
    ],
  },
  {
    key: 'dev',
    tab: 'Developers',
    icon: 'fa-solid fa-code',
    tiles: [
      {
        size: 'lg', peek: 'editor', href: '#feature-f6',
        title: 'Your editor, their agents.',
        blurb: 'Full VS Code in the browser or the 8080 desktop extension - every language and framework.',
      },
      {
        size: 'mid', peek: 'git', href: '#step-07',
        title: 'Branch-per-task, merged by PR',
      },
      { size: 'sm', href: '#step-08', title: 'Security scan per task' },
      { size: 'sm', href: '#feature-f4', title: 'Managed backend on K8s' },
      {
        size: 'wide', accent: true, href: 'vscode:extension/8080-ai.8080-ai-auth',
        title: 'Install the VS Code extension',
      },
      { size: 'sm', href: '#moat', title: 'Flat-cost models' },
      { size: 'sm', href: '#platform', title: 'The full platform' },
    ],
  },
  {
    key: 'data',
    tab: 'Data scientists',
    icon: 'fa-solid fa-flask',
    tiles: [
      {
        size: 'lg', peek: 'agents', href: '#feature-f1',
        title: 'Ship models inside real products.',
        blurb: 'A dedicated AI Engineer agent wires model endpoints, APIs and data stores into working software.',
      },
      {
        size: 'mid', peek: 'k8s', href: '#feature-f4',
        title: 'Your own K8s namespace',
      },
      { size: 'sm', href: '#platform', title: 'Multi-model gateway' },
      { size: 'sm', href: '#step-07', title: 'Isolated workspaces' },
      { size: 'wide', accent: true, href: '/explore', title: 'Open a public architecture' },
      { size: 'sm', href: '#feature-f7', title: 'Auto-scaling infra' },
      { size: 'sm', href: '#process', title: 'The process' },
    ],
  },
  {
    key: 'ent',
    tab: 'Enterprise',
    icon: 'fa-solid fa-building',
    tiles: [
      {
        size: 'lg', peek: 'board', href: '#collaboration',
        title: 'Team-scale control.',
        blurb: 'Org management with owners, admins and members - developers collaborate on one shared view of requirements, designs, tasks and architecture.',
      },
      {
        size: 'mid', peek: 'plan', href: '#step-06',
        title: 'Human review gates',
      },
      { size: 'sm', href: '#feature-f4', title: 'Namespace isolation' },
      { size: 'sm', href: '#moat', title: 'Unit economics' },
      { size: 'wide', accent: true, href: '/pricing', title: 'Plans for teams' },
      { size: 'sm', href: '#compliance', title: 'ISO 27001 · SOC 2 · HIPAA-ready' },
      { size: 'sm', href: '#step-07', title: 'Parallel delivery' },
    ],
  },
  {
    key: 'biz',
    tab: 'Business owners',
    icon: 'fa-solid fa-briefcase',
    tiles: [
      {
        size: 'lg', peek: 'flow', href: '#process',
        title: 'From idea to customers.',
        blurb: 'Follow the whole build in plain language - and own everything at the end.',
      },
      {
        size: 'mid', peek: 'board', href: '#collaboration',
        title: 'One board, every view',
      },
      { size: 'sm', href: '#step-06', title: 'Sign-off gates' },
      { size: 'sm', href: '#step-09', title: 'Deployed for you' },
      { size: 'wide', accent: true, href: '/pricing', title: 'See pricing' },
      { size: 'sm', href: '#moat', title: 'Why it costs less' },
      { size: 'sm', href: '#trust', title: 'Proof & reviews' },
    ],
  },
];

const TileInner: React.FC<{ tile: Tile }> = ({ tile }) => (
  <>
    {tile.peek && (
      <span className="pe-peek" aria-hidden="true">
        <span className="pe-peek__inner">{PEEKS[tile.peek]}</span>
      </span>
    )}
    <span className="pe-tile__text">
      <span className="pe-tile__title">{tile.title}</span>
      {tile.blurb && <span className="pe-tile__blurb">{tile.blurb}</span>}
    </span>
    <i
      className={`pe-tile__go fa-solid ${tile.href.startsWith('#') ? 'fa-arrow-down' : 'fa-arrow-right'}`}
      aria-hidden="true"
    />
  </>
);

const TileEl: React.FC<{ tile: Tile }> = ({ tile }) => {
  const cls = `pe-tile pe-tile--${tile.size}${tile.accent ? ' pe-tile--accent' : ''}`;

  if (tile.href.startsWith('/')) {
    return <Link className={cls} to={tile.href}><TileInner tile={tile} /></Link>;
  }

  return <a className={cls} href={tile.href}><TileInner tile={tile} /></a>;
};

export const PersonaSection: React.FC = () => (
  <section id="personas" className="pe-section" aria-labelledby="persona-heading">
    <div className="pe-inner">
      <div className="pe-header">
        <p className="pe-header__eyebrow">Made for you</p>
        <h2 id="persona-heading" className="pe-header__heading">
          One platform. Five ways in.
          <br />
          <span className="pe-header__sub">Pick who you are - tap a tile to jump to the feature.</span>
        </h2>
      </div>
      <div className="pe-tabs" role="radiogroup" aria-labelledby="persona-heading">
        {PERSONAS.map((p, i) => (
          <input
            key={p.key}
            type="radio"
            name="pe-tab"
            id={`pe-tab-${p.key}`}
            className="pe-radio"
            defaultChecked={i === 0}
          />
        ))}
        <div className="pe-tabbar">
          {PERSONAS.map((p) => (
            <label key={p.key} className="pe-tab" htmlFor={`pe-tab-${p.key}`}>
              <i className={p.icon} aria-hidden="true" />
              <span>{p.tab}</span>
            </label>
          ))}
        </div>
        <div className="pe-panels">
          {PERSONAS.map((p) => (
            <div key={p.key} className={`pe-panel pe-panel--${p.key}`}>
              <div className="pe-bento">
                {p.tiles.map((t) => <TileEl key={t.title} tile={t} />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
